import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { State } from '../shared/core/state';
import { User, UserRoles } from '../shared/core/state/users/models';
import { Subscription } from 'rxjs';
import * as UserActions from '../shared/core/state/users/user.actions';
import * as AppConfigActions from '../shared/core/state/config/config.action';
import { TenantService, TenantInfo } from '../shared/services/tenant.service';
import { DashboardSidebarService } from '../shared/services/dashboard-sidebar.service';

@Component({
  selector: 'black-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css'],
  standalone: false,
})
export class DashboardComponent implements OnInit, OnDestroy {
  private store = inject<Store<State>>(Store);
  public tenantService = inject(TenantService);
  public sidebarService = inject(DashboardSidebarService);

  user: Partial<User> | null = null;
  currentTenant: TenantInfo | null = null;
  sidebarOpen = false;
  desktopCollapsed = false;
  private subscription = new Subscription();

  get tenantLogoUrl(): string | undefined {
    return this.currentTenant?.settings?.branding?.logo || undefined;
  }

  get isDefaultTenant(): boolean {
    return this.tenantService.isDefaultTenant();
  }

  ngOnInit(): void {
    // Hide global site header, mobile dock, and footer while inside dashboard
    this.store.dispatch(AppConfigActions.hideHeader());
    this.store.dispatch(AppConfigActions.hideMobileDock());
    this.store.dispatch(AppConfigActions.hideFooter());

    this.subscription.add(
      this.store
        .select((state) => state.users.user)
        .subscribe((user) => (this.user = user)),
    );

    this.subscription.add(
      this.tenantService.currentTenant$.subscribe((tenant) => {
        this.currentTenant = tenant;
      }),
    );

    // Sync mobile drawer open state
    this.subscription.add(
      this.sidebarService.mobileOpen$.subscribe((open) => {
        this.sidebarOpen = open;
      }),
    );

    // Sync desktop mini-sidebar collapsed state
    this.subscription.add(
      this.sidebarService.desktopCollapsed$.subscribe((collapsed) => {
        this.desktopCollapsed = collapsed;
      }),
    );
  }

  ngOnDestroy(): void {
    // Restore global site header, mobile dock, and footer on exit
    this.store.dispatch(AppConfigActions.showHeader());
    this.store.dispatch(AppConfigActions.showMobileDock());
    this.store.dispatch(AppConfigActions.showFooter());
    this.sidebarService.close();
    this.subscription.unsubscribe();
  }

  get fullName(): string {
    if (this.user?.name) {
      return `${this.user.name.firstName} ${this.user.name.lastName}`;
    }
    return 'User';
  }

  get userInitial(): string {
    if (this.user?.name?.firstName) {
      return this.user.name.firstName.charAt(0).toUpperCase();
    }
    return 'U';
  }

  get isInstructor(): boolean {
    return this.user?.role === UserRoles.INSTRUCTOR;
  }

  get isInstructorOrAdmin(): boolean {
    return (
      this.user?.role === UserRoles.INSTRUCTOR ||
      this.user?.role === UserRoles.ADMIN
    );
  }

  get showCourses(): boolean {
    return this.currentTenant?.settings?.features?.courses !== false;
  }

  get isTenantOwner(): boolean {
    if (this.user?.isSuperAdmin) {
      return true;
    }

    if (this.currentTenant?.ownerId && this.user?.id) {
      return String(this.currentTenant.ownerId) === String(this.user?.id);
    }

    return false;
  }

  get isSoloInstructor(): boolean {
    return this.tenantService.isSoloInstructor();
  }

  get showTutorProfile(): boolean {
    return !this.isSoloInstructor;
  }

  get workspaceSettingsLabel(): string {
    return this.isSoloInstructor ? 'Site Settings' : 'Academy Settings';
  }

  get hasIncompleteTutorProfile(): boolean {
    if (!this.isInstructorOrAdmin) return false;
    const tInfo = {
      ...(this.user?.profile?.tutorInfo || {}),
      ...(this.user?.pendingTutorProfileUpdates?.tutorInfo || {}),
    };
    const bio =
      this.user?.pendingTutorProfileUpdates?.personal?.biography ||
      this.user?.profile?.personal?.biography;
    return (
      !tInfo ||
      !tInfo.subjects?.length ||
      !tInfo.whatsAppNumber ||
      !bio ||
      bio.trim() === ''
    );
  }

  toggleMobileSidebar(): void {
    this.sidebarService.toggleMobile();
  }

  toggleDesktopSidebar(): void {
    this.sidebarService.toggleDesktopCollapsed();
  }

  logout(): void {
    this.store.dispatch(UserActions.logout({ redirect: true }));
  }
}
